"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Info, SlidersHorizontal, X } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { AffiliateFilter } from "@/app/(app)/components/affiliate-filter";
import { ChainsAndTokensFilter } from "@/app/(app)/components/chains-filter";
import { TransactionTypeFilter } from "@/app/(app)/components/transaction-type-filter";
import { Header } from "@/app/(app)/components/header";
import { Referral } from "@/app/(app)/components/referral";
import { ReferralFilter } from "@/app/(app)/components/referral-filter";
import { SearchFilter } from "@/app/(app)/components/search-filter";
import { StatusFilter } from "@/app/(app)/components/status-filter";
import { UsdPriceRangeFilter } from "@/app/(app)/components/usd-price-range-filter";
import { Price } from "@defuse/ui/price";
import { QueryFailed } from "@/components/query-failed";
import type { Volume } from "@/lib/volume";
import { VolumeStaleWarning } from "@/components/volume-stale-warning";
import { LinkWithLoader, useRouterPushWithLoader } from "@/components/route-loader";
import { Short } from "@/components/short";
import { Button } from "@defuse/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@defuse/ui/carousel";
import type { ComboboxItem } from "@defuse/ui/combobox";
import { PopoverSheet, PopoverSheetContent, PopoverSheetTrigger } from "@defuse/ui/popoversheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@defuse/ui/tooltip";
import { useBreakpoint } from "@/hooks/useScreenSize";
import { type StatusTypeVisibleToUser, defaultStatuses } from "@intents-explorer/shared-api/status";
import { useTokens } from "@defuse/ui/tokens-provider";
import { type Tokens, chainLabel, getTokenLabel } from "@intents-explorer/shared-api/tokens";
import type { get5biggestTxsLast24h } from "@/lib/transactions/api/getTransactions";
import type { largestVolumeAffiliates, largestVolumeReferrals } from "@/lib/transactions/transactions";
import { buildTransactionPageHref } from "@intents-explorer/shared-api/transactions/transactionsCommon";
import type { Source } from "@intents-explorer/shared-api/transactions/transactionsApiV1";
import { numToStr, URLSearchParamsToStr } from "@/lib/type-safe-stringify";
import { cn } from "@/lib/utils";
import { pluralize } from "@/lib/pluralize";
import { useScrollToTop } from "./scroll-to-top-provider";
interface FilterState {
  fromChainId: string[] | undefined;
  toChainId: string[] | undefined;
  fromTokenId: string[] | undefined;
  toTokenId: string[] | undefined;
  search: string | undefined;
  depositMemo: string | undefined;
  referral: string | undefined;
  affiliate: string | undefined;
  minUsdPrice: number | undefined;
  maxUsdPrice: number | undefined;
  status: StatusTypeVisibleToUser[] | undefined;
  source: Source[] | undefined;
}
const filtersArray = ["fromChainId", "toChainId", "fromTokenId", "toTokenId", "search", "depositMemo", "referral", "affiliate", "minUsdPrice", "maxUsdPrice", "status", "source"] as const;
interface TableFiltersProps extends FilterState {
  fromVolume?: Volume | undefined;
  toVolume?: Volume | undefined;
  referrals: ComboboxItem[];
  affiliates: ComboboxItem[];
  biggestTxsLast24h: Awaited<ReturnType<typeof get5biggestTxsLast24h>>;
  biggestReferrals: Awaited<ReturnType<typeof largestVolumeReferrals>>;
  biggestAffiliates: Awaited<ReturnType<typeof largestVolumeAffiliates>>;
  volumeFailure: string | undefined;
  statsFailure: string | undefined;
  tokenIdsFromOneClickDB: readonly string[];
}
export function TableFilters(t0) {
  const $ = _c(117);
  let affiliates;
  let biggestAffiliates;
  let biggestReferrals;
  let biggestTxsLast24h;
  let filterParams;
  let fromVolume;
  let referrals;
  let statsFailure;
  let toVolume;
  let tokenIdsFromOneClickDB;
  let volumeFailure;
  if ($[0] !== t0) {
    ({
      fromVolume,
      toVolume,
      referrals,
      affiliates,
      biggestTxsLast24h,
      biggestReferrals,
      biggestAffiliates,
      volumeFailure,
      statsFailure,
      tokenIdsFromOneClickDB,
      ...filterParams
    } = t0);
    $[0] = t0;
    $[1] = affiliates;
    $[2] = biggestAffiliates;
    $[3] = biggestReferrals;
    $[4] = biggestTxsLast24h;
    $[5] = filterParams;
    $[6] = fromVolume;
    $[7] = referrals;
    $[8] = statsFailure;
    $[9] = toVolume;
    $[10] = tokenIdsFromOneClickDB;
    $[11] = volumeFailure;
  } else {
    affiliates = $[1];
    biggestAffiliates = $[2];
    biggestReferrals = $[3];
    biggestTxsLast24h = $[4];
    filterParams = $[5];
    fromVolume = $[6];
    referrals = $[7];
    statsFailure = $[8];
    toVolume = $[9];
    tokenIdsFromOneClickDB = $[10];
    volumeFailure = $[11];
  }
  const [popoverOpen, setPopoverOpen] = useState(false);
  const isDesktop = useBreakpoint("md");
  const pathname = usePathname();
  const routerPushWithLoader = useRouterPushWithLoader();
  const searchParams = useSearchParams();
  const [filterState, setFilterState] = useState(filterParams);
  const [pendingFilters, setPendingFilters] = useState(filterParams);
  const [error, setError] = useState("");
  const {
    scrollToTopRef,
    scrollToTop
  } = useScrollToTop();
  let t1;
  if ($[12] !== filterParams) {
    t1 = {
      filterParams,
      setFilterState,
      setPendingFilters
    };
    $[12] = filterParams;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  useSyncFilterStateWithSearchParams(t1);
  let t2;
  if ($[14] !== pathname || $[15] !== searchParams) {
    t2 = state => {
      const params = new URLSearchParams(URLSearchParamsToStr(searchParams));
      for (const filterName of filtersArray) {
        updateParams(params, filterName, state);
      }
      const query = URLSearchParamsToStr(params);
      return query !== "" ? `${pathname}?${query}` : pathname;
    };
    $[14] = pathname;
    $[15] = searchParams;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const buildUrlFromFilterState = t2;
  let t3;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = newFilterState => {
      setPendingFilters(currentState => ({
        ...currentState,
        ...newFilterState
      }));
    };
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const updatePendingFilters = t3;
  let t4;
  if ($[18] !== buildUrlFromFilterState || $[19] !== error || $[20] !== pendingFilters || $[21] !== routerPushWithLoader || $[22] !== scrollToTop) {
    t4 = () => {
      if (error !== "") {
        return;
      }
      setFilterState(pendingFilters);
      const newUrl = buildUrlFromFilterState(pendingFilters);
      requestAnimationFrame(() => {
        routerPushWithLoader(newUrl, {
          scroll: false
        });
        scrollToTop();
      });
      setPopoverOpen(false);
    };
    $[18] = buildUrlFromFilterState;
    $[19] = error;
    $[20] = pendingFilters;
    $[21] = routerPushWithLoader;
    $[22] = scrollToTop;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const applyPendingFilters = t4;
  let t5;
  if ($[24] !== buildUrlFromFilterState || $[25] !== routerPushWithLoader || $[26] !== scrollToTop) {
    t5 = newFilterState_0 => {
      setFilterState(currentState_0 => {
        const updatedState = {
          ...currentState_0,
          ...newFilterState_0
        };
        const newUrl_0 = buildUrlFromFilterState(updatedState);
        requestAnimationFrame(() => {
          routerPushWithLoader(newUrl_0, {
            scroll: false
          });
          scrollToTop();
        });
        return updatedState;
      });
      setPendingFilters(currentState_1 => ({
        ...currentState_1,
        ...newFilterState_0
      }));
    };
    $[24] = buildUrlFromFilterState;
    $[25] = routerPushWithLoader;
    $[26] = scrollToTop;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const updateFilters = t5;
  const activeFiltersCount = (filterState.fromChainId !== undefined ? 1 : 0) + (filterState.toChainId !== undefined ? 1 : 0) + (filterState.fromTokenId !== undefined ? 1 : 0) + (filterState.toTokenId !== undefined ? 1 : 0) + (filterState.referral !== undefined && filterState.referral !== "" ? 1 : 0) + (filterState.affiliate !== undefined && filterState.affiliate !== "" ? 1 : 0) + (filterState.minUsdPrice !== undefined ? 1 : 0) + (filterState.maxUsdPrice !== undefined ? 1 : 0) + (filterState.status !== undefined && JSON.stringify([...filterState.status].sort()) !== JSON.stringify(defaultStatuses) ? 1 : 0) + (filterState.source !== undefined && !(filterState.source.length === 1 && filterState.source[0] === "swaps") ? 1 : 0);
  let t6;
  if ($[28] !== pathname || $[29] !== routerPushWithLoader || $[30] !== scrollToTop) {
    t6 = () => {
      const clearedState = {
        fromChainId: undefined,
        toChainId: undefined,
        fromTokenId: undefined,
        toTokenId: undefined,
        search: undefined,
        depositMemo: undefined,
        status: undefined,
        referral: undefined,
        affiliate: undefined,
        minUsdPrice: undefined,
        maxUsdPrice: undefined,
        source: undefined
      };
      setFilterState(clearedState);
      setPendingFilters(clearedState);
      requestAnimationFrame(() => {
        routerPushWithLoader(pathname, {
          scroll: false
        });
        scrollToTop();
      });
      setPopoverOpen(false);
    };
    $[28] = pathname;
    $[29] = routerPushWithLoader;
    $[30] = scrollToTop;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const handleClearFilters = t6;
  let t7;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Header />;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== biggestAffiliates || $[34] !== biggestReferrals || $[35] !== biggestTxsLast24h || $[36] !== filterParams.fromChainId || $[37] !== filterParams.fromTokenId || $[38] !== filterParams.toChainId || $[39] !== filterParams.toTokenId || $[40] !== fromVolume || $[41] !== statsFailure || $[42] !== toVolume || $[43] !== volumeFailure) {
    t8 = <div className="to-brand-off-white dark:to-brand-dark-grey w-full bg-linear-to-b from-transparent"><div className="mx-auto flex w-full max-w-3xl flex-col xl:max-w-5xl">{t7}<Stats fromChainId={filterParams.fromChainId} toChainId={filterParams.toChainId} fromTokenId={filterParams.fromTokenId} toTokenId={filterParams.toTokenId} fromVolume={fromVolume} toVolume={toVolume} biggestTxsLast24h={biggestTxsLast24h} biggestReferrals={biggestReferrals} biggestAffiliates={biggestAffiliates} volumeFailure={volumeFailure} statsFailure={statsFailure} /></div></div>;
    $[33] = biggestAffiliates;
    $[34] = biggestReferrals;
    $[35] = biggestTxsLast24h;
    $[36] = filterParams.fromChainId;
    $[37] = filterParams.fromTokenId;
    $[38] = filterParams.toChainId;
    $[39] = filterParams.toTokenId;
    $[40] = fromVolume;
    $[41] = statsFailure;
    $[42] = toVolume;
    $[43] = volumeFailure;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  let t9;
  if ($[45] !== scrollToTopRef) {
    t9 = <div ref={scrollToTopRef} />;
    $[45] = scrollToTopRef;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== filterState.depositMemo || $[48] !== filterState.search || $[49] !== updateFilters) {
    t10 = <SearchFilter key={filterState.search} search={filterState.search} depositMemo={filterState.depositMemo} updateFilters={updateFilters} />;
    $[47] = filterState.depositMemo;
    $[48] = filterState.search;
    $[49] = updateFilters;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  let t11;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SlidersHorizontal className="h-4 w-4" />;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== activeFiltersCount) {
    t12 = activeFiltersCount > 0 && <span className="bg-brand-orange flex h-5 w-5 items-center justify-center rounded-full text-xs text-white">{activeFiltersCount}</span>;
    $[52] = activeFiltersCount;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== t12) {
    t13 = <Button variant="outline" className="flex items-center gap-2">{t11}Filters{t12}</Button>;
    $[54] = t12;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== isDesktop || $[57] !== t13) {
    t14 = <PopoverSheetTrigger isDesktop={isDesktop} asChild={true}>{t13}</PopoverSheetTrigger>;
    $[56] = isDesktop;
    $[57] = t13;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  const t15 = isDesktop ? "end" : "center";
  let t16;
  if ($[59] !== pendingFilters.source) {
    t16 = process.env.NEXT_PUBLIC_HIDE_CONFIDENTIAL !== "true" && <TransactionTypeFilter sources={pendingFilters.source} updateFilters={updatePendingFilters} />;
    $[59] = pendingFilters.source;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== pendingFilters.status) {
    t17 = <StatusFilter statuses={pendingFilters.status} updateFilters={updatePendingFilters} />;
    $[61] = pendingFilters.status;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = process.env.NEXT_PUBLIC_HIDE_CONFIDENTIAL !== "true" && <div className="flex items-center gap-1.5 pt-2"><span className="text-muted-foreground text-xs font-medium">Swap filters</span><Tooltip><TooltipTrigger asChild={true}><Info className="text-muted-foreground h-3.5 w-3.5 cursor-help" /></TooltipTrigger><TooltipContent className="max-w-64">These filters only apply to swap transactions. When any of these are active, masking transactions are excluded from results.</TooltipContent></Tooltip></div>;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== pendingFilters.fromChainId || $[65] !== pendingFilters.fromTokenId || $[66] !== pendingFilters.toChainId || $[67] !== pendingFilters.toTokenId || $[68] !== tokenIdsFromOneClickDB) {
    t19 = <ChainsAndTokensFilter fromChainId={pendingFilters.fromChainId} toChainId={pendingFilters.toChainId} fromTokenId={pendingFilters.fromTokenId} toTokenId={pendingFilters.toTokenId} tokenIdsFromOneClickDB={tokenIdsFromOneClickDB} updateFilters={updatePendingFilters} />;
    $[64] = pendingFilters.fromChainId;
    $[65] = pendingFilters.fromTokenId;
    $[66] = pendingFilters.toChainId;
    $[67] = pendingFilters.toTokenId;
    $[68] = tokenIdsFromOneClickDB;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  let t20;
  if ($[70] !== pendingFilters.referral || $[71] !== referrals) {
    t20 = <ReferralFilter referral={pendingFilters.referral} referrals={referrals} updateFilters={updatePendingFilters} />;
    $[70] = pendingFilters.referral;
    $[71] = referrals;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== affiliates || $[74] !== pendingFilters.affiliate) {
    t21 = <AffiliateFilter affiliate={pendingFilters.affiliate} affiliates={affiliates} updateFilters={updatePendingFilters} />;
    $[73] = affiliates;
    $[74] = pendingFilters.affiliate;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== pendingFilters.maxUsdPrice || $[77] !== pendingFilters.minUsdPrice) {
    t22 = <UsdPriceRangeFilter minUsdPrice={pendingFilters.minUsdPrice} maxUsdPrice={pendingFilters.maxUsdPrice} updateFilters={updatePendingFilters} setError={setError} />;
    $[76] = pendingFilters.maxUsdPrice;
    $[77] = pendingFilters.minUsdPrice;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  let t23;
  if ($[79] !== error) {
    t23 = error !== "" && <p className="text-destructive text-xs">{error}</p>;
    $[79] = error;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  let t24;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <X className="h-4 w-4" />;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  let t25;
  if ($[82] !== handleClearFilters) {
    t25 = <Button variant="outline" onClick={handleClearFilters} className="flex items-center gap-2">{t24}Clear all filters</Button>;
    $[82] = handleClearFilters;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  const t26 = error !== "" ? "cursor-not-allowed opacity-50" : "";
  let t27;
  if ($[84] !== t26) {
    t27 = cn("flex items-center gap-2", t26);
    $[84] = t26;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== applyPendingFilters || $[87] !== t27) {
    t28 = <Button onClick={applyPendingFilters} className={t27}>Apply Filters</Button>;
    $[86] = applyPendingFilters;
    $[87] = t27;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== t25 || $[90] !== t28) {
    t29 = <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{t25}{t28}</div>;
    $[89] = t25;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== t16 || $[93] !== t17 || $[94] !== t19 || $[95] !== t20 || $[96] !== t21 || $[97] !== t22 || $[98] !== t23 || $[99] !== t29) {
    t30 = <div className="flex min-h-0 flex-1 touch-pan-y flex-col gap-4 overflow-y-auto overscroll-contain">{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t29}</div>;
    $[92] = t16;
    $[93] = t17;
    $[94] = t19;
    $[95] = t20;
    $[96] = t21;
    $[97] = t22;
    $[98] = t23;
    $[99] = t29;
    $[100] = t30;
  } else {
    t30 = $[100];
  }
  let t31;
  if ($[101] !== isDesktop || $[102] !== t15 || $[103] !== t30) {
    t31 = <PopoverSheetContent isDesktop={isDesktop} className="h-dvh w-full max-w-none overflow-hidden p-2 pt-10 sm:max-w-none sm:p-4 md:h-auto md:max-h-[calc(100dvh-1rem)] md:w-auto md:max-w-[100vw] md:pt-4" align={t15} sideOffset={8}>{t30}</PopoverSheetContent>;
    $[101] = isDesktop;
    $[102] = t15;
    $[103] = t30;
    $[104] = t31;
  } else {
    t31 = $[104];
  }
  let t32;
  if ($[105] !== isDesktop || $[106] !== popoverOpen || $[107] !== t14 || $[108] !== t31) {
    t32 = <PopoverSheet isDesktop={isDesktop} open={popoverOpen} onOpenChange={setPopoverOpen}>{t14}{t31}</PopoverSheet>;
    $[105] = isDesktop;
    $[106] = popoverOpen;
    $[107] = t14;
    $[108] = t31;
    $[109] = t32;
  } else {
    t32 = $[109];
  }
  let t33;
  if ($[110] !== t10 || $[111] !== t32) {
    t33 = <div className="bg-background/80 sticky top-0 z-10 flex w-full justify-center backdrop-blur-sm"><div className="flex w-full max-w-3xl flex-col items-stretch gap-2 border-b px-2 py-3 sm:px-4 sm:py-5 md:flex-row md:items-center md:justify-between md:gap-4 xl:max-w-5xl">{t10}{t32}</div></div>;
    $[110] = t10;
    $[111] = t32;
    $[112] = t33;
  } else {
    t33 = $[112];
  }
  let t34;
  if ($[113] !== t33 || $[114] !== t8 || $[115] !== t9) {
    t34 = <>{t8}{t9}{t33}</>;
    $[113] = t33;
    $[114] = t8;
    $[115] = t9;
    $[116] = t34;
  } else {
    t34 = $[116];
  }
  return t34;
}
function useSyncFilterStateWithSearchParams(t0) {
  const $ = _c(16);
  const {
    filterParams,
    setFilterState,
    setPendingFilters
  } = t0;
  const {
    fromChainId,
    toChainId,
    fromTokenId,
    toTokenId,
    search,
    depositMemo,
    status,
    referral,
    affiliate,
    minUsdPrice,
    maxUsdPrice,
    source
  } = filterParams;
  let t1;
  let t2;
  if ($[0] !== affiliate || $[1] !== depositMemo || $[2] !== fromChainId || $[3] !== fromTokenId || $[4] !== maxUsdPrice || $[5] !== minUsdPrice || $[6] !== referral || $[7] !== search || $[8] !== setFilterState || $[9] !== setPendingFilters || $[10] !== source || $[11] !== status || $[12] !== toChainId || $[13] !== toTokenId) {
    t1 = () => {
      const newState = {
        fromChainId,
        toChainId,
        fromTokenId,
        toTokenId,
        search,
        depositMemo,
        status,
        referral,
        affiliate,
        minUsdPrice,
        maxUsdPrice,
        source
      };
      setFilterState(newState);
      setPendingFilters(newState);
    };
    t2 = [fromChainId, toChainId, fromTokenId, toTokenId, search, depositMemo, status, referral, affiliate, minUsdPrice, maxUsdPrice, source, setFilterState, setPendingFilters];
    $[0] = affiliate;
    $[1] = depositMemo;
    $[2] = fromChainId;
    $[3] = fromTokenId;
    $[4] = maxUsdPrice;
    $[5] = minUsdPrice;
    $[6] = referral;
    $[7] = search;
    $[8] = setFilterState;
    $[9] = setPendingFilters;
    $[10] = source;
    $[11] = status;
    $[12] = toChainId;
    $[13] = toTokenId;
    $[14] = t1;
    $[15] = t2;
  } else {
    t1 = $[14];
    t2 = $[15];
  }
  useEffect(t1, t2);
}
function updateParams(params: URLSearchParams, filterName: keyof FilterState, state: FilterState) {
  const filter = state[filterName];
  if (typeof filter === "string") {
    params.set(filterName, filter);
  } else if (typeof filter === "number") {
    params.set(filterName, numToStr(filter));
  } else if (Array.isArray(filter)) {
    params.delete(filterName);
    for (const value of filter) {
      params.append(filterName, value);
    }
  } else {
    const _undefined: undefined = filter;
    params.delete(filterName);
  }
}
function Stats(t0) {
  const $ = _c(34);
  const {
    fromChainId,
    toChainId,
    fromVolume,
    toVolume,
    fromTokenId,
    toTokenId,
    biggestTxsLast24h,
    biggestReferrals,
    biggestAffiliates,
    volumeFailure,
    statsFailure
  } = t0;
  const tokens = useTokens();
  let t1;
  if ($[0] !== fromChainId || $[1] !== fromTokenId || $[2] !== tokens) {
    t1 = getFromOrTo(fromTokenId, fromChainId, tokens);
    $[0] = fromChainId;
    $[1] = fromTokenId;
    $[2] = tokens;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const from = t1;
  let t2;
  if ($[4] !== toChainId || $[5] !== toTokenId || $[6] !== tokens) {
    t2 = getFromOrTo(toTokenId, toChainId, tokens);
    $[4] = toChainId;
    $[5] = toTokenId;
    $[6] = tokens;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const to = t2;
  const visibleFromVolume = (from === undefined && to === undefined || from !== undefined) && fromVolume !== undefined ? fromVolume : undefined;
  const visibleToVolume = to !== undefined && to !== from ? toVolume : undefined;
  let t3;
  if ($[8] !== volumeFailure) {
    t3 = volumeFailure !== undefined && <QueryFailed what="volume" correlationId={volumeFailure} />;
    $[8] = volumeFailure;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== statsFailure) {
    t4 = statsFailure !== undefined && <QueryFailed what="24h stats and filter options" correlationId={statsFailure} />;
    $[10] = statsFailure;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== from || $[13] !== visibleFromVolume) {
    t5 = visibleFromVolume !== undefined && <VolumeBox chainOrToken={from} lastDay={visibleFromVolume.lastDay} last7Days={visibleFromVolume.last7Days} last30Days={visibleFromVolume.last30Days} allTime={visibleFromVolume.allTime} fetchedAt={visibleFromVolume.fetchedAt} isStale={visibleFromVolume.isStale} />;
    $[12] = from;
    $[13] = visibleFromVolume;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== to || $[16] !== visibleToVolume) {
    t6 = visibleToVolume !== undefined && <VolumeBox chainOrToken={to} lastDay={visibleToVolume.lastDay} last7Days={visibleToVolume.last7Days} last30Days={visibleToVolume.last30Days} allTime={visibleToVolume.allTime} fetchedAt={visibleToVolume.fetchedAt} isStale={visibleToVolume.isStale} />;
    $[15] = to;
    $[16] = visibleToVolume;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== biggestTxsLast24h) {
    t7 = <BiggestTxs biggestTxsLast24h={biggestTxsLast24h} />;
    $[18] = biggestTxsLast24h;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== biggestAffiliates || $[21] !== biggestReferrals) {
    t8 = <LargestReferralsAndAffiliates biggestReferrals={biggestReferrals} biggestAffiliates={biggestAffiliates} />;
    $[20] = biggestAffiliates;
    $[21] = biggestReferrals;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== visibleFromVolume || $[24] !== visibleToVolume) {
    t9 = (visibleFromVolume !== undefined || visibleToVolume !== undefined) && <a className="hover:shadow-primary/20 bg-brand-orange inline-flex w-fit items-center justify-center rounded-lg px-3 py-1.5 text-sm font-bold text-white transition-all hover:shadow-md" href="https://dune.com/near/near-intents" rel="noopener noreferrer" target="_blank">More Stats</a>;
    $[23] = visibleFromVolume;
    $[24] = visibleToVolume;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t10 = <div className="flex flex-col items-center gap-1 px-4 py-4">{t3}{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
}
function getFromOrTo(tokenIds: string[] | undefined, chainIds: string[] | undefined, tokens: Tokens) {
  const firstTokenId = tokenIds?.[0];
  if (firstTokenId !== undefined) {
    return getTokenLabel(tokens, firstTokenId);
  }
  const firstChainId = chainIds?.[0];
  return firstChainId === undefined ? undefined : chainLabel(firstChainId);
}
function BiggestTxs(t0) {
  const $ = _c(10);
  const {
    biggestTxsLast24h
  } = t0;
  const [hoveredTx, setHoveredTx] = useState(null);
  const [focusedTx, setFocusedTx] = useState(null);
  if (biggestTxsLast24h.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h1 className="text-brand-orange text-center text-2xl font-bold md:text-3xl">24h Top Swaps</h1>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== biggestTxsLast24h || $[2] !== focusedTx || $[3] !== hoveredTx) {
    let t3;
    if ($[5] !== focusedTx || $[6] !== hoveredTx) {
      t3 = (tx, i) => {
        const isActive = hoveredTx === tx.depositAddress || focusedTx === tx.depositAddress;
        return <LinkWithLoader href={buildTransactionPageHref(tx)} key={tx.depositAddress} className={cn("bg-background hover:shadow-primary/5 col-span-2 grid grid-cols-[1fr_auto_1fr] items-center gap-1 rounded-xl px-2 py-4 transition-all hover:shadow-lg sm:gap-2 sm:p-4 md:py-5", i === 3 ? "col-start-1 md:col-start-3 xl:col-start-2" : "", i === 4 ? "col-start-1 md:col-start-2 xl:col-start-4" : "")} onMouseEnter={() => {
          setHoveredTx(tx.depositAddress);
        }} onMouseLeave={() => {
          setHoveredTx(null);
        }} onTouchStart={() => {
          setHoveredTx(tx.depositAddress);
        }} onTouchEnd={() => {
          setHoveredTx(null);
        }} onFocus={() => {
          setFocusedTx(tx.depositAddress);
        }} onBlur={() => {
          setFocusedTx(null);
        }}><div className="relative flex justify-center"><Price type={tx.depositType} price={tx.amountInFormatted} defuseAssetId={tx.originAsset} usdAmount={tx.amountInUsd} length={4} showUsdAmount={isActive} /></div><span className="text-muted-foreground text-xs md:text-sm"><ArrowRight className="h-4 w-4" /></span><div className="relative flex justify-center"><Price type={tx.recipientType} price={tx.amountOutFormatted} defuseAssetId={tx.destinationAsset} usdAmount={tx.amountOutUsd} length={4} showUsdAmount={isActive} /></div></LinkWithLoader>;
      };
      $[5] = focusedTx;
      $[6] = hoveredTx;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = biggestTxsLast24h.map(t3);
    $[1] = biggestTxsLast24h;
    $[2] = focusedTx;
    $[3] = hoveredTx;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <div className="flex w-full flex-col items-center gap-2 py-2 sm:gap-3 sm:py-4 md:gap-6">{t1}<div className="grid w-full cursor-default grid-cols-1 gap-2 md:grid-cols-4 md:gap-2 xl:grid-cols-6">{t2}</div></div>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function LargestReferralsAndAffiliates(t0) {
  const $ = _c(17);
  const {
    biggestReferrals,
    biggestAffiliates
  } = t0;
  const [isInteracting, setIsInteracting] = useState(false);
  if (biggestReferrals.length === 0 && biggestAffiliates.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      loop: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isInteracting) {
    t2 = isInteracting ? {} : {
      autoScrollMs: 9000
    };
    $[1] = isInteracting;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsInteracting(true);
    };
    t4 = () => {
      setIsInteracting(false);
    };
    t5 = () => {
      setIsInteracting(true);
    };
    t6 = () => {
      setIsInteracting(false);
    };
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== biggestReferrals) {
    t7 = biggestReferrals.length > 0 && <CarouselItem><div className="flex w-full flex-col items-center gap-2 py-2 sm:gap-3 sm:py-4 xl:gap-6"><h1 className="text-brand-orange text-center text-2xl font-bold xl:text-3xl">24h Largest Volume Referrals</h1><div className="grid w-full cursor-default grid-cols-4 gap-2 xl:grid-cols-5">{biggestReferrals.map(_temp)}</div></div></CarouselItem>;
    $[7] = biggestReferrals;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== biggestAffiliates) {
    t8 = biggestAffiliates.length > 0 && <CarouselItem><div className="flex w-full flex-col items-center gap-2 py-2 sm:gap-3 sm:py-4 xl:gap-6"><h1 className="text-brand-orange text-center text-2xl font-bold xl:text-3xl">24h Largest Volume Affiliates</h1><div className="grid w-full cursor-default grid-cols-4 gap-2 xl:grid-cols-5">{biggestAffiliates.map(_temp2)}</div></div></CarouselItem>;
    $[9] = biggestAffiliates;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <CarouselContent>{t7}{t8}</CarouselContent>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t2 || $[15] !== t9) {
    t10 = <Carousel className="w-full" opts={t1} {...t2} onMouseEnter={t3} onMouseLeave={t4} onTouchStart={t5} onTouchEnd={t6}>{t9}</Carousel>;
    $[14] = t2;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}
function _temp2(t0, i_0) {
  const {
    numberOfTxs: numberOfTxs_0,
    recipient,
    volume: volume_0
  } = t0;
  return <div key={recipient} className={cn("bg-background hover:shadow-primary/5 col-span-2 flex items-center justify-center gap-2 rounded-xl p-3 transition-all hover:shadow-lg sm:gap-3 xl:col-span-1", i_0 === 3 ? "col-start-3 xl:col-start-4" : "", i_0 === 4 ? "col-start-2 xl:col-start-5" : "")}><div className="text-center"><Affiliate affiliate={recipient} /><div className="text-muted-foreground text-xs">{numberOfTxs_0}{" "}{pluralize(numberOfTxs_0, "transaction")}</div><div className="text-primary text-lg font-bold xl:text-xl">${Math.round(volume_0).toLocaleString()}</div></div></div>;
}
function _temp(t0, i) {
  const {
    referral,
    numberOfTxs,
    volume
  } = t0;
  return <div key={referral === null || referral === "" ? `referral-${numToStr(i)}` : referral} className={cn("bg-background hover:shadow-primary/5 col-span-2 flex items-center justify-center gap-2 rounded-xl p-3 transition-all hover:shadow-lg sm:gap-3 xl:col-span-1", i === 3 ? "col-start-3 xl:col-start-4" : "", i === 4 ? "col-start-2 xl:col-start-5" : "")}><div className="text-center"><Referral referral={referral} affiliates={undefined} className="mb-2" length={20} /><div className="text-muted-foreground text-xs">{numberOfTxs}{" "}{pluralize(numberOfTxs, "transaction")}</div><div className="text-primary text-lg font-bold xl:text-xl">${Math.round(volume).toLocaleString()}</div></div></div>;
}
function VolumeBox(t0) {
  const $ = _c(24);
  const {
    chainOrToken,
    lastDay,
    last7Days,
    last30Days,
    allTime,
    fetchedAt,
    isStale
  } = t0;
  let t1;
  if ($[0] !== chainOrToken) {
    t1 = chainOrToken !== undefined && chainOrToken !== "" ? <span className="flex items-center gap-2 sm:gap-3"><span className="bg-brand-orange rounded-lg px-3 py-1 text-center text-white">{chainOrToken}</span>Volume</span> : "Total Volume";
    $[0] = chainOrToken;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <h1 className="text-brand-orange text-2xl font-bold md:text-3xl">{t1}</h1>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== allTime) {
    t3 = <VolumeCard label="All time" value={allTime} />;
    $[4] = allTime;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== lastDay) {
    t4 = <VolumeCard label="24h Volume" value={lastDay} />;
    $[6] = lastDay;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== last7Days) {
    t5 = <VolumeCard label="7D Volume" value={last7Days} />;
    $[8] = last7Days;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== last30Days) {
    t6 = <VolumeCard label="30D Volume" value={last30Days} />;
    $[10] = last30Days;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className="grid w-full cursor-default grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-4 xl:gap-4">{t3}{t4}{t5}{t6}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== fetchedAt || $[18] !== isStale) {
    t8 = <VolumeStaleWarning fetchedAt={fetchedAt} isStale={isStale} />;
    $[17] = fetchedAt;
    $[18] = isStale;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t2 || $[21] !== t7 || $[22] !== t8) {
    t9 = <div className="flex w-full flex-col items-center gap-2 py-2 sm:gap-3 sm:py-4 md:gap-6">{t2}{t7}{t8}</div>;
    $[20] = t2;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
function VolumeCard(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-muted-foreground text-xs md:text-sm">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-primary text-xl font-bold transition-transform group-hover:scale-105 md:text-2xl">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="group bg-background hover:shadow-primary/5 relative flex flex-col items-center rounded-xl p-2 transition-all hover:shadow-lg sm:p-4 md:p-6">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function Affiliate(t0) {
  const $ = _c(7);
  const {
    affiliate
  } = t0;
  const pathname = usePathname();
  const {
    scrollToTop
  } = useScrollToTop();
  let t1;
  if ($[0] !== affiliate || $[1] !== pathname) {
    t1 = affiliate === "" ? undefined : (() => {
      const params = new URLSearchParams();
      params.set("affiliate", affiliate);
      return `${pathname}?${URLSearchParamsToStr(params)}`;
    })();
    $[0] = affiliate;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const link = t1;
  let t2;
  if ($[3] !== affiliate || $[4] !== link || $[5] !== scrollToTop) {
    t2 = <Short text={affiliate} showCopy={false} length={20} className="border-brand-orange text-brand-orange mb-2 cursor-pointer rounded-full border px-2 py-1 text-[10px] font-normal" link={link} linkClassName="text-inherit" onInternalLinkClick={scrollToTop} scroll={false} />;
    $[3] = affiliate;
    $[4] = link;
    $[5] = scrollToTop;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}